import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, Eye, Image as ImageIcon, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  createAdminDisplayProofType,
  deleteAdminDisplayProofById,
  exportAdminDisplayProofsCsv,
  getAdminDisplayProofById,
  getAdminDisplayProofTypes,
  getAdminDisplayProofs,
  getDashboardPromoters,
  resolveAssetUrl,
  updateAdminDisplayProofById,
  updateAdminDisplayProofType,
  deleteAdminDisplayProofType,
} from "../../../services/api";
import { useNotification } from "../../../hooks/useNotification";
import { DataTable } from "../Table";
import { TableSkeleton } from "../Table/Skeleton";
import Modal from "../Modal/Modal";
import ConfirmDialog from "../components/ConfirmDialog";
import "./DisplayProofsView.css";

const DEFAULT_FILTERS = {
  promoterIds: [],
  startDate: "",
  endDate: "",
  proofType: "all",
};

const DEFAULT_PAGINATION = {
  limit: 10,
  offset: 0,
  total: 0,
};
const DEFAULT_SERVER_SORT = {
  key: "submitted_at",
  direction: "desc",
};

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const LEGACY_TO_CANONICAL_PROOF_TYPE = Object.freeze({
  station: "common_display",
  shelf: "display_corner",
  other: "others",
});

const formatPromoterLabel = (promoter) =>
  promoter?.email ? `${promoter.full_name} (${promoter.email})` : promoter?.full_name || "";

const resolvePromoterFromOptions = (rawInput, options) => {
  const normalizedInput = String(rawInput || "").trim();
  if (!normalizedInput) return null;

  const normalizedLower = normalizedInput.toLowerCase();
  const list = Array.isArray(options) ? options : [];

  const exactMatch =
    list.find((promoter) => {
      const helperValue = formatPromoterLabel(promoter).toLowerCase();
      const fullName = String(promoter.full_name || "").trim().toLowerCase();
      const email = String(promoter.email || "").trim().toLowerCase();
      return (
        helperValue === normalizedLower ||
        fullName === normalizedLower ||
        email === normalizedLower
      );
    }) || null;

  if (exactMatch) return exactMatch;

  const partialMatches = list.filter((promoter) => {
    const helperValue = formatPromoterLabel(promoter).toLowerCase();
    const fullName = String(promoter.full_name || "").toLowerCase();
    const email = String(promoter.email || "").toLowerCase();
    return (
      helperValue.includes(normalizedLower) ||
      fullName.includes(normalizedLower) ||
      email.includes(normalizedLower)
    );
  });

  return partialMatches.length === 1 ? partialMatches[0] : null;
};

const normalizeProofTypeCode = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");
  return LEGACY_TO_CANONICAL_PROOF_TYPE[normalized] || normalized;
};

const toTitleCase = (value) =>
  String(value || "")
    .split(/[_\s]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getProofTypeText = (t, proofType, proofTypeMap = {}) => {
  const normalized = normalizeProofTypeCode(proofType);
  if (proofTypeMap[normalized]) return proofTypeMap[normalized];
  if (normalized === "common_display") return t("admin.displayProofs.proofTypes.commonDisplay");
  if (normalized === "display_corner") return t("admin.displayProofs.proofTypes.displayCorner");
  if (normalized === "promo_area") return t("admin.displayProofs.proofTypes.promoArea");
  if (normalized === "others") return t("admin.displayProofs.proofTypes.others");
  return normalized ? toTitleCase(normalized) : t("admin.common.unknown");
};

const getProofItems = (proof) => (Array.isArray(proof?.items) ? proof.items : []);

// An item is a competitor observation, an own-brand model, or neither
// ("untagged" — the promoter picked no model and ticked no competitor).
const getItemSubjectLabel = (t, item) => {
  if (item.subject_type === "competitor") return t("admin.competitors.competitorLabel");
  if (item.subject_type === "untagged") return t("admin.displayProofs.subjectLabel");
  return t("admin.displayProofs.modelLabel");
};

const getItemSubjectValue = (t, item) => {
  if (item.subject_type === "competitor")
    return item.competitor?.name || t("admin.displayProofs.na");
  if (item.subject_type === "untagged") return t("admin.displayProofs.untagged");
  return item.product?.model_name || t("admin.displayProofs.noModel");
};

const getProofModelsText = (proof) =>
  getProofItems(proof)
    .map((item) =>
      item.subject_type === "competitor"
        ? item.competitor?.name
        : item.product?.model_name,
    )
    .filter(Boolean)
    .join(", ");

const getProofNotesText = (proof) =>
  getProofItems(proof)
    .map((item) => item.notes)
    .filter(Boolean)
    .join(" | ");

export default function DisplayProofsView({
  user = null,
  searchIntent = null,
  onSearchIntentHandled = null,
}) {
  const { t, i18n } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const locale = i18n.language === "ms" ? "ms-MY" : "en-MY";
  const isSuperAdmin = user?.role === "super_admin";

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [serverSort, setServerSort] = useState(DEFAULT_SERVER_SORT);
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [promoterInput, setPromoterInput] = useState("");
  const [promoterOptions, setPromoterOptions] = useState([]);
  const [selectedPromoters, setSelectedPromoters] = useState([]);
  const [promoterOptionsLoading, setPromoterOptionsLoading] = useState(false);
  const [showPromoterSuggestions, setShowPromoterSuggestions] = useState(false);
  const promoterHelperRef = useRef(null);

  const [selectedProofId, setSelectedProofId] = useState(null);
  const [selectedProof, setSelectedProof] = useState(null);
  const [selectedProofLoading, setSelectedProofLoading] = useState(false);
  const [showEditProofModal, setShowEditProofModal] = useState(false);
  const [editingProof, setEditingProof] = useState(null);
  const [savingProof, setSavingProof] = useState(false);
  const [showDeleteProofModal, setShowDeleteProofModal] = useState(false);
  const [pendingDeleteProof, setPendingDeleteProof] = useState(null);
  const [deletingProofId, setDeletingProofId] = useState(null);
  const [proofEditForm, setProofEditForm] = useState({
    proof_type: "",
  });
  const [imageLoadingState, setImageLoadingState] = useState({});
  const [proofTypeOptions, setProofTypeOptions] = useState([]);
  const [proofTypeOptionsLoading, setProofTypeOptionsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [isManageTypesOpen, setIsManageTypesOpen] = useState(false);
  const [managedTypes, setManagedTypes] = useState([]);
  const [managedTypesLoading, setManagedTypesLoading] = useState(false);
  const [savingType, setSavingType] = useState(false);
  const savingTypeLockRef = useRef(false);
  const [deletingTypeId, setDeletingTypeId] = useState(null);
  const [togglingTypeId, setTogglingTypeId] = useState(null);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [showDeleteTypeModal, setShowDeleteTypeModal] = useState(false);
  const [pendingDeleteType, setPendingDeleteType] = useState(null);
  const [typeForm, setTypeForm] = useState({
    name: "",
    code: "",
    is_active: true,
  });

  const proofTypeLabelMap = useMemo(() => {
    const map = {};
    (Array.isArray(proofTypeOptions) ? proofTypeOptions : []).forEach((type) => {
      const normalized = normalizeProofTypeCode(type?.code);
      if (!normalized) return;
      map[normalized] = type?.name || toTitleCase(normalized);
    });
    return map;
  }, [proofTypeOptions]);

  useEffect(() => {
    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        setPromoterOptionsLoading(true);
        const response = await getDashboardPromoters({
          q: promoterInput,
          limit: 20,
        });

        if (!isCancelled) {
          setPromoterOptions(response?.data || []);
        }
      } catch (_) {
        if (!isCancelled) {
          setPromoterOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setPromoterOptionsLoading(false);
        }
      }
    }, 220);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [promoterInput]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (promoterHelperRef.current && !promoterHelperRef.current.contains(event.target)) {
        setShowPromoterSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const fetchFilterProofTypes = useCallback(async () => {
    try {
      setProofTypeOptionsLoading(true);
      const response = await getAdminDisplayProofTypes({ includeInactive: true });
      setProofTypeOptions(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setProofTypeOptions([]);
      showError(error?.response?.data?.error || t("admin.displayProofs.failedToLoadTypes"));
    } finally {
      setProofTypeOptionsLoading(false);
    }
  }, [showError, t]);

  const fetchManagedTypes = useCallback(async () => {
    try {
      setManagedTypesLoading(true);
      const response = await getAdminDisplayProofTypes({ includeInactive: true });
      setManagedTypes(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setManagedTypes([]);
      showError(error?.response?.data?.error || t("admin.displayProofs.failedToLoadTypes"));
    } finally {
      setManagedTypesLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    fetchFilterProofTypes();
  }, [fetchFilterProofTypes]);

  useEffect(() => {
    if (filters.proofType === "all") return;
    const validCodes = new Set(
      (Array.isArray(proofTypeOptions) ? proofTypeOptions : []).map((type) =>
        normalizeProofTypeCode(type?.code),
      ),
    );
    if (!validCodes.has(normalizeProofTypeCode(filters.proofType))) {
      setFilters((previous) => ({
        ...previous,
        proofType: "all",
      }));
    }
  }, [filters.proofType, proofTypeOptions]);

  const fetchProofs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAdminDisplayProofs({
        promoterIds: filters.promoterIds,
        startDate: filters.startDate,
        endDate: filters.endDate,
        proofType: filters.proofType,
        sortBy: serverSort.key,
        sortDirection: serverSort.direction,
        limit: pagination.limit,
        offset: pagination.offset,
      });

      setProofs(response?.data || []);
      setPagination((previous) => ({
        ...previous,
        total: response?.pagination?.total || 0,
      }));
    } catch (error) {
      showError(error?.response?.data?.error || t("admin.displayProofs.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [
    filters.promoterIds,
    filters.startDate,
    filters.endDate,
    filters.proofType,
    serverSort.key,
    serverSort.direction,
    pagination.limit,
    pagination.offset,
    showError,
    t,
  ]);

  useEffect(() => {
    fetchProofs();
  }, [fetchProofs]);

  useEffect(() => {
    if (!selectedProofId) {
      setSelectedProof(null);
      setImageLoadingState({});
      return;
    }
    

    let isCancelled = false;
    const fetchProofDetail = async () => {
      try {
        setSelectedProofLoading(true);
        const response = await getAdminDisplayProofById(selectedProofId);
        if (!isCancelled) {
          setSelectedProof(response?.data || null);
        }
      } catch (error) {
        if (!isCancelled) {
          setSelectedProof(null);
          showError(error?.response?.data?.error || t("admin.displayProofs.failedToLoadDetail"));
        }
      } finally {
        if (!isCancelled) {
          setSelectedProofLoading(false);
        }
      }
      
    };

    fetchProofDetail();
    return () => {
      isCancelled = true;
    };
  }, [selectedProofId, showError, t]);

  useEffect(() => {
    if (!selectedProof || !Array.isArray(selectedProof.items)) {
      setImageLoadingState({});
      return;
    }

    const nextState = {};
    selectedProof.items.forEach((item) => {
      (Array.isArray(item.images) ? item.images : []).forEach((image) => {
        if (image?.image_id !== undefined && image?.image_id !== null) {
          nextState[image.image_id] = true;
        }
      });
    });
    setImageLoadingState(nextState);
  }, [selectedProof]);

  useEffect(() => {
    if (!searchIntent) return;

    const item = searchIntent.item || null;
    const promoterId = Number.parseInt(item?.user_id, 10);
    const promoterName = String(item?.promoter_name || "").trim();
    const promoterEmail = String(item?.promoter_email || "").trim();
    const proofType = normalizeProofTypeCode(item?.proof_type);
    const allowedTypes = new Set(
      (Array.isArray(proofTypeOptions) ? proofTypeOptions : []).map((type) => type.code),
    );

    if (Number.isInteger(promoterId) && promoterId > 0) {
      const promoterOption = {
        user_id: promoterId,
        full_name: promoterName || promoterEmail || `Promoter #${promoterId}`,
        email: promoterEmail,
      };
      setSelectedPromoters([promoterOption]);
      setPromoterInput(formatPromoterLabel(promoterOption));
      setFilters((previous) => ({
        ...previous,
        promoterIds: [promoterId],
        proofType: allowedTypes.has(proofType) ? proofType : previous.proofType,
      }));
    }

    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));

    if (typeof onSearchIntentHandled === "function" && searchIntent.intentId) {
      onSearchIntentHandled(searchIntent.intentId);
    }
  }, [onSearchIntentHandled, proofTypeOptions, searchIntent]);

  const handleServerPaginationChange = ({ limit, offset }) => {
    setPagination((previous) => ({
      ...previous,
      limit,
      offset,
    }));
  };

  const handleItemsPerPageChange = (event) => {
    const nextLimit = Number.parseInt(event.target.value, 10) || DEFAULT_PAGINATION.limit;
    setPagination((previous) => ({
      ...previous,
      limit: nextLimit,
      offset: 0,
    }));
  };

  const addPromoterFilter = (promoter) => {
    const promoterId = String(promoter.user_id);
    setFilters((previous) => {
      if (previous.promoterIds.map(String).includes(promoterId)) return previous;
      return {
        ...previous,
        promoterIds: [...previous.promoterIds, promoter.user_id],
      };
    });
    setSelectedPromoters((previous) => {
      if (previous.some((item) => String(item.user_id) === promoterId)) return previous;
      return [...previous, promoter];
    });
    setPromoterInput("");
    setShowPromoterSuggestions(false);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  };

  const removePromoterFilter = (promoterId) => {
    setFilters((previous) => ({
      ...previous,
      promoterIds: previous.promoterIds.filter((id) => String(id) !== String(promoterId)),
    }));
    setSelectedPromoters((previous) =>
      previous.filter((item) => String(item.user_id) !== String(promoterId)),
    );
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  };

  const handlePromoterInputEnter = async () => {
    const trimmedInput = String(promoterInput || "").trim();
    if (!trimmedInput) return;

    const localMatch = resolvePromoterFromOptions(trimmedInput, promoterOptions);
    if (localMatch) {
      addPromoterFilter(localMatch);
      return;
    }

    try {
      setPromoterOptionsLoading(true);
      const response = await getDashboardPromoters({ q: trimmedInput, limit: 20 });
      const remoteOptions = response?.data || [];
      setPromoterOptions(remoteOptions);
      const remoteMatch = resolvePromoterFromOptions(trimmedInput, remoteOptions);
      if (remoteMatch) {
        addPromoterFilter(remoteMatch);
      }
    } catch (_) {
      // no-op
    } finally {
      setPromoterOptionsLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSelectedPromoters([]);
    setPromoterInput("");
    setShowPromoterSuggestions(false);
    setPagination((previous) => ({
      ...previous,
      offset: 0,
    }));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await exportAdminDisplayProofsCsv({
        promoterIds: filters.promoterIds,
        proofType: filters.proofType,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });

      const url = URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().split("T")[0];
      link.setAttribute("download", `display_proofs_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSuccess(t("admin.displayProofs.exportSuccess", { defaultValue: "Export successful" }));
    } catch (error) {
      console.error("Error exporting display proofs CSV:", error);
      showError(t("admin.displayProofs.exportFailed", { defaultValue: "Export failed. Please try again." }));
    } finally {
      setExporting(false);
    }
  };

  const formatDateTime = useCallback(
    (value) => {
      if (!value) return t("admin.displayProofs.na");
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return t("admin.displayProofs.na");
      return date.toLocaleString(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    [locale, t],
  );

  const columns = useMemo(
    () => [
      { label: t("admin.displayProofs.table.date"), sortKey: "submitted_at" },
      { label: t("admin.displayProofs.table.promoter"), sortKey: "promoter_name" },
      { label: t("admin.displayProofs.table.location"), sortKey: "location" },
      { label: t("admin.displayProofs.table.proofType"), sortKey: "proof_type" },
      { label: t("admin.displayProofs.table.model") },
      { label: t("admin.displayProofs.table.images") },
      { label: t("admin.displayProofs.table.notes") },
      { label: t("admin.displayProofs.table.action") },
    ],
    [t],
  );

  const managedTypeColumns = useMemo(
    () => [
      { label: t("admin.displayProofs.manage.fields.name") },
      { label: t("admin.displayProofs.manage.fields.code") },
      { label: t("admin.displayProofs.manage.fields.status") },
      { label: t("admin.displayProofs.table.action") },
    ],
    [t],
  );

  const renderProofRow = (proof) => {
    const proofModelsText = getProofModelsText(proof);
    const proofNotesText = getProofNotesText(proof);
    const itemCount = proof.item_count || getProofItems(proof).length;

    return (
    <tr key={proof.proof_id}>
      <td>
        {formatDateTime(proof.submitted_at)}
        {proof.last_edited_at ? (
          <span
            className="admin-display-proofs-edited-badge"
            title={t("admin.displayProofs.lastEditedAt", {
              date: formatDateTime(proof.last_edited_at),
            })}
          >
            {t("admin.displayProofs.editedBadge")}
          </span>
        ) : null}
      </td>
      <td>
        <div className="admin-display-proofs-promoter">
          <span className="admin-display-proofs-promoter-name">
            {proof?.promoter?.full_name || t("admin.displayProofs.unknownPromoter")}
          </span>
          <span className="admin-display-proofs-promoter-email">
            {proof?.promoter?.email || t("admin.displayProofs.na")}
          </span>
        </div>
      </td>
      <td>{proof.outlet_name || t("admin.displayProofs.na")}</td>
      <td>
        <span className={`admin-display-proofs-type is-${proof.proof_type || "unknown"}`}>
          {getProofTypeText(t, proof.proof_type, proofTypeLabelMap)}
        </span>
      </td>
      <td title={proofModelsText || undefined}>
        {proofModelsText || t("admin.displayProofs.noModel")}
        {itemCount > 1 ? (
          <span className="admin-display-proofs-item-count"> ({itemCount})</span>
        ) : null}
      </td>
      <td>
        <div className="admin-display-proofs-image-meta">
          <ImageIcon size={14} />
          <span>{proof.image_count || 0}</span>
        </div>
      </td>
      <td className="admin-display-proofs-notes-cell">
        {proofNotesText ? (
          <div className="admin-display-proofs-notes-wrapper">
            <span
              className={`admin-display-proofs-notes-preview ${
                expandedNotes[proof.proof_id] ? "is-expanded" : ""
              }`}
              title={proofNotesText}
            >
              {proofNotesText}
            </span>
            {proofNotesText.length > 120 ? (
              <button
                type="button"
                className="admin-display-proofs-notes-toggle"
                onClick={() =>
                  setExpandedNotes((previous) => ({
                    ...previous,
                    [proof.proof_id]: !previous[proof.proof_id],
                  }))
                }
              >
                {expandedNotes[proof.proof_id]
                  ? t("admin.displayProofs.table.readLess")
                  : t("admin.displayProofs.table.readMore")}
              </button>
            ) : null}
          </div>
        ) : (
          t("admin.displayProofs.noNotes")
        )}
      </td>
      <td>
        <div className="admin-display-proofs-row-actions">
          <button
            type="button"
            className="admin-display-proofs-view-btn"
            onClick={() => setSelectedProofId(proof.proof_id)}
          >
            <Eye size={14} />
            {t("admin.displayProofs.viewDetails")}
          </button>
          {isSuperAdmin ? (
            <>
              <button
                type="button"
                className="admin-display-proofs-view-btn admin-display-proof-types-edit-btn"
                onClick={() => openEditProofModal(proof)}
              >
                <Pencil size={14} />
                {t("admin.common.edit")}
              </button>
              <button
                type="button"
                className="admin-display-proof-types-delete-btn"
                onClick={() => openDeleteProofModal(proof)}
              >
                <Trash2 size={14} />
                {t("admin.common.delete")}
              </button>
            </>
          ) : null}
        </div>
      </td>
    </tr>
    );
  };

  const closeDetailModal = () => {
    if (selectedProofLoading) return;
    setSelectedProofId(null);
    setSelectedProof(null);
  };

  const openEditProofModal = (proof) => {
    if (!isSuperAdmin || !proof?.proof_id) return;
    setEditingProof(proof);
    setProofEditForm({
      proof_type: normalizeProofTypeCode(proof?.proof_type),
    });
    setShowEditProofModal(true);
  };

  const closeEditProofModal = (force = false) => {
    if (savingProof && !force) return;
    setShowEditProofModal(false);
    setEditingProof(null);
    setProofEditForm({
      proof_type: "",
    });
  };

  const handleProofEditInputChange = (event) => {
    const { name, value } = event.target;
    setProofEditForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSaveEditedProof = async (event) => {
    event.preventDefault();
    if (!editingProof?.proof_id) return;

    const nextProofType = normalizeProofTypeCode(proofEditForm.proof_type);

    if (!nextProofType) {
      showError(t("admin.displayProofs.manage.validation.typeRequired", {
        defaultValue: "Proof type is required.",
      }));
      return;
    }

    try {
      setSavingProof(true);
      await updateAdminDisplayProofById(editingProof.proof_id, {
        proof_type: nextProofType,
      });
      showSuccess(t("admin.displayProofs.manage.messages.updated", {
        defaultValue: "Display proof updated successfully.",
      }));
      closeEditProofModal(true);
      await fetchProofs();
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.displayProofs.manage.messages.saveFailed", {
            defaultValue: "Failed to save display proof.",
          }),
      );
    } finally {
      setSavingProof(false);
    }
  };

  const openDeleteProofModal = (proof) => {
    if (!isSuperAdmin || !proof?.proof_id) return;
    setPendingDeleteProof(proof);
    setShowDeleteProofModal(true);
  };

  const handleConfirmDeleteProof = async () => {
    if (!pendingDeleteProof?.proof_id) return;

    try {
      setDeletingProofId(pendingDeleteProof.proof_id);
      await deleteAdminDisplayProofById(pendingDeleteProof.proof_id);
      showSuccess(t("admin.displayProofs.manage.messages.deleted", {
        defaultValue: "Display proof deleted successfully.",
      }));
      if (Number(selectedProofId) === Number(pendingDeleteProof.proof_id)) {
        closeDetailModal();
      }
      setShowDeleteProofModal(false);
      setPendingDeleteProof(null);
      await fetchProofs();
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.displayProofs.manage.messages.deleteFailed", {
            defaultValue: "Failed to delete display proof.",
          }),
      );
    } finally {
      setDeletingProofId(null);
    }
  };

  const resetTypeForm = useCallback(() => {
    setTypeForm({
      name: "",
      code: "",
      is_active: true,
    });
    setEditingTypeId(null);
  }, []);

  const openManageTypesModal = async () => {
    if (!isSuperAdmin) return;
    setIsManageTypesOpen(true);
    await fetchManagedTypes();
  };

  const closeManageTypesModal = () => {
    if (savingType || deletingTypeId) return;
    setIsManageTypesOpen(false);
    resetTypeForm();
  };

  const handleTypeInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setTypeForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditManagedType = (type) => {
    setEditingTypeId(type.proof_type_id);
    setTypeForm({
      name: type?.name || "",
      code: type?.code || "",
      is_active: Boolean(type?.is_active),
    });
  };

  const handleCancelTypeEdit = () => {
    resetTypeForm();
  };

  const handleSaveType = async (event) => {
    event.preventDefault();
    if (savingTypeLockRef.current) return;

    const trimmedName = String(typeForm.name || "").trim();
    if (!trimmedName) {
      showError(t("admin.displayProofs.manage.validation.nameRequired"));
      return;
    }

    const payload = {
      name: trimmedName,
      is_active: Boolean(typeForm.is_active),
    };

    const trimmedCode = String(typeForm.code || "").trim();
    if (!editingTypeId && trimmedCode) {
      payload.code = trimmedCode;
    }

    try {
      savingTypeLockRef.current = true;
      setSavingType(true);

      if (editingTypeId) {
        await updateAdminDisplayProofType(editingTypeId, payload);
        showSuccess(t("admin.displayProofs.manage.messages.updated"));
      } else {
        await createAdminDisplayProofType(payload);
        showSuccess(t("admin.displayProofs.manage.messages.created"));
      }

      await Promise.all([fetchManagedTypes(), fetchFilterProofTypes()]);
      resetTypeForm();
    } catch (error) {
      showError(
        error?.response?.data?.error || t("admin.displayProofs.manage.messages.saveFailed"),
      );
    } finally {
      savingTypeLockRef.current = false;
      setSavingType(false);
    }
  };

  const handleDeleteManagedType = (type) => {
    setPendingDeleteType(type);
    setShowDeleteTypeModal(true);
  };

  const handleConfirmDeleteType = async () => {
    if (!pendingDeleteType) return;
    try {
      setDeletingTypeId(pendingDeleteType.proof_type_id);
      const response = await deleteAdminDisplayProofType(pendingDeleteType.proof_type_id);
      const responseType = response?.data;
      const deletedOrArchived =
        responseType && typeof responseType === "object"
          ? t("admin.displayProofs.manage.messages.archived")
          : t("admin.displayProofs.manage.messages.deleted");
      showSuccess(deletedOrArchived);

      if (editingTypeId === pendingDeleteType.proof_type_id) {
        resetTypeForm();
      }

      setShowDeleteTypeModal(false);
      setPendingDeleteType(null);
      await Promise.all([fetchManagedTypes(), fetchFilterProofTypes()]);
    } catch (error) {
      showError(
        error?.response?.data?.error || t("admin.displayProofs.manage.messages.deleteFailed"),
      );
    } finally {
      setDeletingTypeId(null);
    }
  };

  const handleToggleManagedTypeStatus = async (type) => {
    try {
      setTogglingTypeId(type.proof_type_id);
      await updateAdminDisplayProofType(type.proof_type_id, {
        is_active: !type.is_active,
      });
      showSuccess(t("admin.displayProofs.manage.messages.updated"));
      await Promise.all([fetchManagedTypes(), fetchFilterProofTypes()]);
    } catch (error) {
      showError(
        error?.response?.data?.error || t("admin.displayProofs.manage.messages.saveFailed"),
      );
    } finally {
      setTogglingTypeId(null);
    }
  };

  const isEditingType = Boolean(editingTypeId);
  const isSavingCreate = savingType && !isEditingType;
  const isSavingUpdate = savingType && isEditingType;
  const typeSubmitLabel = isSavingUpdate
    ? t("admin.displayProofs.manage.updating")
    : isSavingCreate
      ? t("admin.displayProofs.manage.creating")
      : isEditingType
        ? t("admin.displayProofs.manage.update")
        : t("admin.displayProofs.manage.create");

  return (
    <div className="admin-display-proofs-content">
      <div className="admin-display-proofs-header">
        <div>
          <h2 className="admin-section-title">{t("admin.displayProofs.title")}</h2>
          <p className="admin-section-subtitle">{t("admin.displayProofs.subtitle")}</p>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={handleExport}
            disabled={exporting || loading}
            style={{ backgroundColor: "#000", color: "#fff", borderColor: "#000" }}
          >
            <Download size={14} />
            {exporting
              ? t("admin.displayProofs.exporting", { defaultValue: "Exporting..." })
              : t("admin.displayProofs.exportCsv", { defaultValue: "Export CSV" })}
          </button>
          {isSuperAdmin ? (
            <button
              type="button"
              className="admin-btn-primary admin-display-proofs-manage-btn"
              onClick={openManageTypesModal}
            >
              <Pencil size={14} />
              {t("admin.displayProofs.manage.button")}
            </button>
          ) : null}
        </div>
      </div>

      <div className="admin-display-proofs-filters">
        <div className="admin-display-proofs-filter-row">
          <div className="admin-display-proofs-field">
            <label>{t("admin.displayProofs.filters.promoter")}</label>
            <div className="admin-display-proofs-helper" ref={promoterHelperRef}>
              <input
                type="text"
                value={promoterInput}
                className="admin-display-proofs-input"
                placeholder={t("admin.displayProofs.filters.searchPromoter")}
                onFocus={() => setShowPromoterSuggestions(true)}
                onChange={(event) => {
                  setPromoterInput(event.target.value);
                  setShowPromoterSuggestions(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setShowPromoterSuggestions(false);
                  } else if (event.key === "Enter") {
                    event.preventDefault();
                    handlePromoterInputEnter();
                  }
                }}
              />
              {showPromoterSuggestions && (
                <div className="admin-display-proofs-suggestions">
                  {promoterOptionsLoading ? (
                    <div className="admin-display-proofs-suggestion-empty">
                      {t("admin.common.loading")}
                    </div>
                  ) : promoterOptions.length > 0 ? (
                    promoterOptions.map((promoter) => (
                      <button
                        key={promoter.user_id}
                        type="button"
                        className="admin-display-proofs-suggestion-item"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          addPromoterFilter(promoter);
                        }}
                      >
                        {formatPromoterLabel(promoter)}
                      </button>
                    ))
                  ) : (
                    <div className="admin-display-proofs-suggestion-empty">
                      {t("admin.displayProofs.filters.noPromotersFound")}
                    </div>
                  )}
                </div>
              )}
            </div>
            {selectedPromoters.length > 0 && (
              <div className="admin-display-proofs-chips">
                {selectedPromoters.map((promoter) => (
                  <button
                    key={`proof-promoter-${promoter.user_id}`}
                    type="button"
                    className="admin-display-proofs-chip"
                    onClick={() => removePromoterFilter(promoter.user_id)}
                  >
                    {formatPromoterLabel(promoter)}
                    <X size={14} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="admin-display-proofs-field">
            <label>{t("admin.displayProofs.filters.startDate")}</label>
            <input
              type="date"
              className="admin-display-proofs-input"
              value={filters.startDate}
              onChange={(event) => {
                setFilters((previous) => ({
                  ...previous,
                  startDate: event.target.value,
                }));
                setPagination((previous) => ({
                  ...previous,
                  offset: 0,
                }));
              }}
            />
          </div>

          <div className="admin-display-proofs-field">
            <label>{t("admin.displayProofs.filters.endDate")}</label>
            <input
              type="date"
              className="admin-display-proofs-input"
              value={filters.endDate}
              onChange={(event) => {
                setFilters((previous) => ({
                  ...previous,
                  endDate: event.target.value,
                }));
                setPagination((previous) => ({
                  ...previous,
                  offset: 0,
                }));
              }}
            />
          </div>

          <div className="admin-display-proofs-field">
            <label>{t("admin.displayProofs.filters.proofType")}</label>
            <select
              className="admin-display-proofs-input admin-display-proofs-select"
              value={filters.proofType}
              disabled={proofTypeOptionsLoading}
              onChange={(event) => {
                setFilters((previous) => ({
                  ...previous,
                  proofType: event.target.value,
                }));
                setPagination((previous) => ({
                  ...previous,
                  offset: 0,
                }));
              }}
            >
              <option value="all">{t("admin.displayProofs.filters.allProofTypes")}</option>
              {proofTypeOptions.map((type) => (
                <option key={type.proof_type_id || type.code} value={type.code}>
                  {getProofTypeText(t, type.code, proofTypeLabelMap)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="admin-display-proofs-filter-actions">
          <button type="button" className="admin-btn-secondary" onClick={clearFilters}>
            {t("admin.common.clear")}
          </button>
          <div className="admin-display-proofs-items-per-page">
            <label htmlFor="displayProofsItemsPerPage">{t("admin.common.itemsPerPage")}</label>
            <select
              id="displayProofsItemsPerPage"
              className="admin-display-proofs-input admin-display-proofs-select"
              value={pagination.limit}
              onChange={handleItemsPerPageChange}
              disabled={loading}
            >
              {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <DataTable
        data={proofs}
        columns={columns}
        renderRow={renderProofRow}
        showHeader={false}
        showSearch={false}
        loading={loading}
        serverMode
        serverSort={serverSort}
        onServerSortChange={(nextSort) => {
          setServerSort({
            key: nextSort?.key || DEFAULT_SERVER_SORT.key,
            direction: nextSort?.direction || DEFAULT_SERVER_SORT.direction,
          });
          setPagination((previous) => ({
            ...previous,
            offset: 0,
          }));
        }}
        serverPagination={{
          total: pagination.total,
          limit: pagination.limit,
          offset: pagination.offset,
        }}
        onServerPaginationChange={handleServerPaginationChange}
        containerClassName="admin-users-section admin-display-proofs-table-card"
        tableClassName="admin-users-table admin-display-proofs-table"
        emptyMessage={t("admin.displayProofs.noData")}
      />

      <div className="admin-display-proofs-pagination-meta">
        <span>
          {t("admin.displayProofs.showing", {
            start: pagination.total === 0 ? 0 : pagination.offset + 1,
            end: Math.min(pagination.offset + pagination.limit, pagination.total),
            total: pagination.total,
          })}
        </span>
      </div>

      <Modal isOpen={Boolean(selectedProofId)} onClose={closeDetailModal} size="large">
        <div className="admin-display-proofs-detail-modal">
          {selectedProofLoading ? (
            <div className="admin-display-proofs-detail-skeleton" aria-hidden="true">
              <div className="admin-display-proofs-skeleton-line is-title"></div>
              <div className="admin-display-proofs-skeleton-grid">
                <div className="admin-display-proofs-skeleton-card">
                  <div className="admin-display-proofs-skeleton-line is-label"></div>
                  <div className="admin-display-proofs-skeleton-line is-value"></div>
                  <div className="admin-display-proofs-skeleton-line is-value-sm"></div>
                </div>
                <div className="admin-display-proofs-skeleton-card">
                  <div className="admin-display-proofs-skeleton-line is-label"></div>
                  <div className="admin-display-proofs-skeleton-line is-value"></div>
                </div>
                <div className="admin-display-proofs-skeleton-card">
                  <div className="admin-display-proofs-skeleton-line is-label"></div>
                  <div className="admin-display-proofs-skeleton-line is-value"></div>
                </div>
                <div className="admin-display-proofs-skeleton-card">
                  <div className="admin-display-proofs-skeleton-line is-label"></div>
                  <div className="admin-display-proofs-skeleton-line is-value"></div>
                </div>
              </div>
              <div className="admin-display-proofs-skeleton-card">
                <div className="admin-display-proofs-skeleton-line is-label"></div>
                <div className="admin-display-proofs-skeleton-line is-paragraph"></div>
                <div className="admin-display-proofs-skeleton-line is-paragraph short"></div>
              </div>
              <div className="admin-display-proofs-skeleton-line is-label wide"></div>
              <div className="admin-display-proofs-skeleton-gallery">
                <div className="admin-display-proofs-skeleton-image"></div>
                <div className="admin-display-proofs-skeleton-image"></div>
                <div className="admin-display-proofs-skeleton-image"></div>
              </div>
            </div>
          ) : selectedProof ? (
            <>
              <h3 className="admin-display-proofs-detail-title">
                {t("admin.displayProofs.detailTitle")}
              </h3>
              <div className="admin-display-proofs-detail-grid">
                <div className="admin-display-proofs-detail-item">
                  <span>{t("admin.displayProofs.table.promoter")}</span>
                  <strong>
                    {selectedProof?.promoter?.full_name || t("admin.displayProofs.unknownPromoter")}
                  </strong>
                  <small>{selectedProof?.promoter?.email || t("admin.displayProofs.na")}</small>
                </div>
                <div className="admin-display-proofs-detail-item">
                  <span>{t("admin.displayProofs.table.location")}</span>
                  <strong>{selectedProof.outlet_name || t("admin.displayProofs.na")}</strong>
                  {selectedProof.outlet_address ? (
                    <small>{selectedProof.outlet_address}</small>
                  ) : null}
                </div>
                <div className="admin-display-proofs-detail-item">
                  <span>{t("admin.displayProofs.table.submittedAt")}</span>
                  <strong>{formatDateTime(selectedProof.submitted_at)}</strong>
                  {selectedProof.last_edited_at ? (
                    <small>
                      {t("admin.displayProofs.lastEditedAt", {
                        date: formatDateTime(selectedProof.last_edited_at),
                      })}
                    </small>
                  ) : null}
                </div>
                <div className="admin-display-proofs-detail-item">
                  <span>{t("admin.displayProofs.table.proofType")}</span>
                  <strong>{getProofTypeText(t, selectedProof.proof_type, proofTypeLabelMap)}</strong>
                </div>
              </div>

              <div className="admin-display-proofs-detail-item-list">
                {(Array.isArray(selectedProof.items) ? selectedProof.items : []).map(
                  (item, index) => (
                    <div
                      key={item.proof_item_id || `item-${index}`}
                      className="admin-display-proofs-detail-item-card"
                    >
                      <h4>{t("se.displayProof.itemTitle", { n: index + 1 })}</h4>
                      <div className="admin-display-proofs-detail-item-meta">
                        <div>
                          <span>{getItemSubjectLabel(t, item)}</span>
                          <strong>{getItemSubjectValue(t, item)}</strong>
                        </div>
                        <div>
                          <span>{t("admin.displayProofs.table.color")}</span>
                          <strong>{item.product?.colour || t("admin.displayProofs.na")}</strong>
                        </div>
                        <div>
                          <span>{t("admin.displayProofs.table.promotion")}</span>
                          <strong>{item.is_promotion ? "✅" : "-"}</strong>
                        </div>
                        <div>
                          <span>{t("admin.displayProofs.table.notes")}</span>
                          <p>{item.notes || t("admin.displayProofs.noNotes")}</p>
                        </div>
                      </div>

                      {Array.isArray(item.images) && item.images.length > 0 ? (
                        <div className="admin-display-proofs-gallery">
                          {item.images.map((image) => (
                            <a
                              key={image.image_id}
                              href={resolveAssetUrl(image.image_url)}
                              target="_blank"
                              rel="noreferrer"
                              className="admin-display-proofs-gallery-item"
                            >
                              <div className="admin-display-proofs-gallery-media">
                                {imageLoadingState[image.image_id] && (
                                  <div
                                    className="admin-display-proofs-gallery-image-skeleton"
                                    aria-hidden="true"
                                  />
                                )}
                                <img
                                  key={`${selectedProofId}-${image.image_id}`}
                                  src={resolveAssetUrl(image.image_url)}
                                  alt={`${t("admin.displayProofs.proofImageAlt")} ${image.sort_order + 1}`}
                                  className={`admin-display-proofs-gallery-img ${
                                    imageLoadingState[image.image_id] ? "is-loading" : "is-loaded"
                                  }`}
                                  onLoad={(e) => {
                                    if (e.target.complete) {
                                      setImageLoadingState((prev) => ({
                                        ...prev,
                                        [image.image_id]: false,
                                      }));
                                    }
                                  }}
                                  onError={() => {
                                    setImageLoadingState((previous) => ({
                                      ...previous,
                                      [image.image_id]: false,
                                    }));
                                  }}
                                />
                              </div>
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="admin-display-proofs-no-images">
                          {t("admin.displayProofs.noImages")}
                        </p>
                      )}
                    </div>
                  ),
                )}
              </div>
            </>
          ) : (
            <p>{t("admin.displayProofs.detailUnavailable")}</p>
          )}
        </div>
      </Modal>

      {isSuperAdmin ? (
      <Modal isOpen={showEditProofModal} onClose={closeEditProofModal} size="medium">
        <form className="admin-display-proof-edit-form" onSubmit={handleSaveEditedProof}>
          <h3 className="admin-display-proofs-detail-title">
            {t("admin.displayProofs.editTitle", { defaultValue: "Edit Display Proof" })}
          </h3>

          <label className="admin-display-proof-edit-field">
            <span>{t("admin.displayProofs.table.proofType")}</span>
            <select
              name="proof_type"
              className="admin-display-proofs-input admin-display-proofs-select"
              value={proofEditForm.proof_type}
              onChange={handleProofEditInputChange}
              disabled={savingProof}
              required
            >
              <option value="">{t("admin.common.select", { defaultValue: "Select" })}</option>
              {proofTypeOptions.map((type) => (
                <option key={`edit-proof-type-${type.proof_type_id || type.code}`} value={type.code}>
                  {getProofTypeText(t, type.code, proofTypeLabelMap)}
                </option>
              ))}
            </select>
          </label>

          <div className="admin-display-proof-edit-actions">
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={closeEditProofModal}
              disabled={savingProof}
            >
              {t("admin.common.cancel")}
            </button>
            <button type="submit" className="admin-btn-primary" disabled={savingProof}>
              {savingProof
                ? t("admin.common.processing")
                : t("admin.common.save", { defaultValue: "Save" })}
            </button>
          </div>
        </form>
      </Modal>
      ) : null}

      {isSuperAdmin ? (
      <Modal
        isOpen={showDeleteProofModal}
        onClose={() => {
          if (deletingProofId) return;
          setShowDeleteProofModal(false);
          setPendingDeleteProof(null);
        }}
        size="small"
      >
        <ConfirmDialog
          title={t("admin.displayProofs.deleteTitle", { defaultValue: "Delete Display Proof" })}
          message={t("admin.displayProofs.deleteMessage", {
            defaultValue: "This will permanently delete the selected display proof.",
          })}
          variant="danger"
          confirmText={t("admin.common.delete")}
          cancelText={t("admin.common.cancel")}
          processingText={t("admin.common.processing")}
          onConfirm={handleConfirmDeleteProof}
          onCancel={() => {
            if (deletingProofId) return;
            setShowDeleteProofModal(false);
            setPendingDeleteProof(null);
          }}
          isLoading={Boolean(deletingProofId)}
        />
      </Modal>
      ) : null}

      {isSuperAdmin ? (
      <Modal isOpen={isManageTypesOpen} onClose={closeManageTypesModal} size="large">
        <div className="admin-display-proof-types-modal">
          <div className="admin-display-proof-types-header">
            <h3 className="admin-display-proofs-detail-title">
              {t("admin.displayProofs.manage.title")}
            </h3>
            <p className="admin-display-proof-types-subtitle">
              {t("admin.displayProofs.manage.subtitle")}
            </p>
          </div>

          <form className="admin-display-proof-types-form" onSubmit={handleSaveType}>
            <div className="admin-display-proof-types-form-grid">
              <label className="admin-display-proof-types-field">
                <span>{t("admin.displayProofs.manage.fields.name")}</span>
                <input
                  type="text"
                  name="name"
                  className="admin-display-proofs-input"
                  value={typeForm.name}
                  onChange={handleTypeInputChange}
                  maxLength={120}
                  disabled={savingType}
                />
              </label>

              <label className="admin-display-proof-types-field">
                <span>{t("admin.displayProofs.manage.fields.code")}</span>
                <input
                  type="text"
                  name="code"
                  className="admin-display-proofs-input"
                  value={typeForm.code}
                  onChange={handleTypeInputChange}
                  placeholder={t("admin.displayProofs.manage.codePlaceholder")}
                  maxLength={50}
                  disabled={savingType || Boolean(editingTypeId)}
                />
              </label>
            </div>

            <div className="admin-display-proof-types-toolbar">
              <div className="admin-display-proof-types-status-group">
                <span className="admin-display-proof-types-status-label">
                  {t("admin.displayProofs.manage.fields.status")}
                </span>
                <label className="admin-display-proof-types-checkbox">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={typeForm.is_active}
                    onChange={handleTypeInputChange}
                    disabled={savingType}
                  />
                  <span className="admin-display-proof-types-switch-slider" />
                  <span className="admin-display-proof-types-switch-text">
                    {typeForm.is_active
                      ? t("admin.displayProofs.manage.status.active")
                      : t("admin.displayProofs.manage.status.inactive")}
                  </span>
                </label>
              </div>

              <div className="admin-display-proof-types-form-actions">
                {editingTypeId ? (
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={handleCancelTypeEdit}
                    disabled={savingType}
                  >
                    {t("admin.displayProofs.manage.cancelEdit")}
                  </button>
                ) : null}
                <button
                  type="submit"
                  className="admin-btn-primary admin-display-proof-types-submit-btn"
                  disabled={savingType}
                  aria-busy={savingType}
                >
                  {savingType ? (
                    <Loader2 size={14} className="admin-display-proof-types-submit-icon is-spinning" />
                  ) : isEditingType ? (
                    <Pencil size={14} />
                  ) : (
                    <Plus size={14} />
                  )}
                  {typeSubmitLabel}
                </button>
              </div>
            </div>
          </form>

          <div className="admin-display-proof-types-list-wrapper">
            {managedTypesLoading ? (
              <TableSkeleton
                columns={managedTypeColumns}
                rowCount={4}
                tableClassName="admin-display-proof-types-table"
                loading
              />
            ) : managedTypes.length === 0 ? (
              <p className="admin-display-proof-types-empty">
                {t("admin.displayProofs.manage.empty")}
              </p>
            ) : (
              <table className="admin-display-proof-types-table">
                <thead>
                  <tr>
                    <th>{t("admin.displayProofs.manage.fields.name")}</th>
                    <th>{t("admin.displayProofs.manage.fields.code")}</th>
                    <th>{t("admin.displayProofs.manage.fields.status")}</th>
                    <th>{t("admin.displayProofs.table.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {managedTypes.map((type) => {
                    const isDeleting = deletingTypeId === type.proof_type_id;
                    const isToggling = togglingTypeId === type.proof_type_id;
                    return (
                      <tr key={type.proof_type_id || type.code}>
                        <td>{type.name}</td>
                        <td>
                          <code>{type.code}</code>
                        </td>
                        <td>
                          <button
                            type="button"
                            className={`admin-display-proof-types-row-switch ${
                              type.is_active ? "is-on" : "is-off"
                            }`}
                            onClick={() => handleToggleManagedTypeStatus(type)}
                            disabled={savingType || isDeleting || isToggling}
                            aria-label={t("admin.displayProofs.manage.fields.status")}
                          >
                            <span className="admin-display-proof-types-row-switch-track">
                              <span className="admin-display-proof-types-row-switch-knob" />
                            </span>
                            <span className="admin-display-proof-types-row-switch-label">
                              {type.is_active
                                ? t("admin.displayProofs.manage.status.active")
                                : t("admin.displayProofs.manage.status.inactive")}
                            </span>
                          </button>
                        </td>
                        <td>
                          <div className="admin-display-proof-types-actions">
                            <button
                              type="button"
                              className="admin-display-proofs-view-btn admin-display-proof-types-edit-btn"
                              onClick={() => handleEditManagedType(type)}
                              disabled={savingType || isDeleting}
                            >
                              <Pencil size={14} />
                              {t("admin.common.edit")}
                            </button>
                            <button
                              type="button"
                              className="admin-display-proof-types-delete-btn"
                              onClick={() => handleDeleteManagedType(type)}
                              disabled={savingType || isDeleting}
                            >
                              <Trash2 size={14} />
                              {isDeleting
                                ? t("admin.common.loading")
                                : t("admin.common.delete")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>
      ) : null}

      {isSuperAdmin ? (
      <Modal
        isOpen={showDeleteTypeModal}
        onClose={() => {
          if (deletingTypeId) return;
          setShowDeleteTypeModal(false);
          setPendingDeleteType(null);
        }}
        size="small"
      >
        <ConfirmDialog
          title={t("admin.displayProofs.manage.deleteTitle")}
          message={t("admin.displayProofs.manage.confirmDelete", {
            name: pendingDeleteType?.name || pendingDeleteType?.code || "",
          })}
          variant="danger"
          confirmText={t("admin.common.delete")}
          cancelText={t("admin.common.cancel")}
          processingText={t("admin.common.processing")}
          onConfirm={handleConfirmDeleteType}
          onCancel={() => {
            if (deletingTypeId) return;
            setShowDeleteTypeModal(false);
            setPendingDeleteType(null);
          }}
          isLoading={Boolean(deletingTypeId)}
        />
      </Modal>
      ) : null}
    </div>
  );
}
